import { Button, IconButton, Modal, Paper, Tooltip } from "@mui/material";
import { shade } from "polished";
import { FC, useEffect } from "react";
import { Control, useForm } from "react-hook-form";
import { FaShareSquare } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import styled from "styled-components";
import { resendPatientAccess } from "../../api/rooms/resendPatientAccess";
import { IModalProps } from "../../common/interfaces/IModalProps";
import { useFullpageLoading } from "../../hooks/useFullPageLoading";
import { useInfo } from "../../hooks/useInfo";
import { showToast } from "../../utils/showToast";
import PhoneInput from "react-phone-number-input/react-hook-form";
import { parsePhoneNumber, isPossiblePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import pt_BR from "react-phone-number-input/locale/pt-BR.json";

const ModalWrapper = styled(Paper)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: fit-content;
  max-width: 90%;
`;

const ModalHeader = styled("div")`
  color: ${(props) => props.theme.colors.primary};
  position: relative;
  padding: 16px 32px;
  border-bottom: 1px solid #eeeeee;
  font-size: 18px;
  height: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  text-align: center;
  .MuiIconButton-root {
    position: absolute;
    top: 4px;
    right: 4px;
  }
`;

const ModalContent = styled("form")`
  padding: 28px;
  box-sizing: border-box;
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
  gap: 12px;
`;

const ContentTitle = styled("span")`
  text-align: center;
  font-size: 13px;
`;

const FieldsWrapper = styled("div")`
  width: 100%;
  display: flex;
  flex-flow: column;
  gap: 8px;
`;

const Field = styled("div")`
  display: flex;
  flex-flow: column;
  gap: 4px;
  input {
    box-sizing: border-box;
    min-height: 30px;
    padding: 4px 6px;
    text-align: center;
    color: #858585;
    font-family: inherit;
    font-size: 14px;
    border: 1px solid #d5d5d5;
    border-radius: 4px;
  }
`;

const FieldLabel = styled("label")`
  font-size: 14px;
`;

const ButtonsWrapper = styled("div")`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 8px;
  gap: 8px;
`;

const ActionButton = styled(Button)`
  && {
    font-size: 16px;
    color: white;
    text-transform: none;
    display: flex;
    width: 100%;
  }
`;

const WppButton = styled(ActionButton)`
  && {
    background-color: #87b87f;
    &:hover {
      background-color: ${shade(0.2, "#87b87f")};
    }
  }
`;

const SendButton = styled(ActionButton)`
  && {
    background-color: ${(props) => props.theme.colors.secondary};
    &:hover {
      background-color: ${(props) => shade(0.2, props.theme.colors.secondary)};
    }
    &:disabled {
      color: rgba(255, 255, 255, 0.6);  
    }
  }
`;

const ErrorMessage = styled("div")`
  height: 15px;
  text-align: center;
  line-height: 14px;
  span {
    font-size: 14px;
    color: rgb(255, 38, 38);
  }
`;

const ButtonWrapper = styled("div")`
  width: 100%;
`;

const ButtonTooltip = styled(Tooltip)``;

type SendLinkForm = {
  phone: string;
  email: string;
};

type SendType = "whatsapp" | "sms" | "email";

enum SEND_TYPE {
  WHATSAPP = "whatsapp",
  SMS = "sms",
  EMAIL = "email"
}

/**
 * Modal para reenviar o link de acesso
 */
const SendLinkModal: FC<IModalProps> = ({ opened = false, handleClose }) => {
  const { telemedicineData } = useInfo();
  const { handleShowFullPageLoading } = useFullpageLoading();

  const phoneNumber = telemedicineData?.patientPhone?.replace(/\D/g, "") || "";
  const ddi = String(telemedicineData?.patientPhoneDdi || "");
  const initialPhoneNumber = `+${ddi}${phoneNumber}`;

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError,
    watch,
    setValue,
  } = useForm<SendLinkForm>({
    defaultValues: {
      email: telemedicineData.patientEmail || "",
      phone: initialPhoneNumber,
    },
  });

  useEffect(() => {
    setValue("phone", initialPhoneNumber);
    setValue("email", telemedicineData.patientEmail || "");
  }, [opened]);

  const onSubmit = (data: SendLinkForm, type: SendType) => {
    const phone = data?.phone || "";

    if (type !== "email" && phone && !isPossiblePhoneNumber(phone)) {
      setError("phone", {
        type: "minLength",
        message: "Número de celular inválido",
      });
      return;
    }

    if (type === "whatsapp" && phone === "") {
      setError("phone", {
        type: "required",
        message: "É necessário informar o telefone do paciente",
      });
      return;
    }

    if (type === "sms" && phone === "") {
      setError("phone", {
        type: "validate",
        message: "É necessário informar o telefone do paciente",
      });
      return;
    }

    if (type === "email" && data.email === "") {
      setError("email", {
        type: "validate",
        message: "É necessário informar o e-mail do paciente",
      });
      return;
    }

    const parsedPhone = parsePhoneNumber(phone);
    const nationalNumber = parsedPhone?.nationalNumber;
    const ddi = parsedPhone?.countryCallingCode;

    if ((!ddi || !nationalNumber) && type != "email") {
      setError("phone", {
        type: "validate",
        message: "Número de celular inválido",
      });
      return;
    }

    handleShowFullPageLoading(true);

    resendPatientAccess({
      type: type,
      phone: nationalNumber,
      ddi: ddi,
      email: data.email,
    })
      .then((response) => {
        handleShowFullPageLoading(false);
        if (type === "sms" || type === "email") {
          showToast("success", {
            text: "Link enviado com sucesso!",
            timer: 5000,
          });
        }

        if (type === "whatsapp" && response.data.whatsappUrl) {
          window.open(response.data.whatsappUrl, "_blank");
        }
        handleClose();
      })
      .catch(() => {
        handleShowFullPageLoading(false);
        showToast("error", {
          text: "Ocorreu um problema ao tentar enviar o link, verifique as informações e tente novamente",
          timer: 10000,
          showConfirmButton: true,
          confirmButtonText: "OK",
        });
      });
  };

  const handleCloseEvent = () => {
    clearErrors();
    handleClose();
  };

  return (
    <Modal open={opened} onClose={handleCloseEvent}>
      <ModalWrapper>
        <ModalHeader>
          <span>Envio de link para teleatendimento</span>
          <IconButton onClick={handleCloseEvent}>
            <MdClose size={20} />
          </IconButton>
        </ModalHeader>
        <ModalContent>
          <ContentTitle>CONTATOS DO PACIENTE</ContentTitle>
          <FieldsWrapper>
            <Field>
              <FieldLabel htmlFor="phone">
                <b>Celular</b> ( com DDD e DDI )
              </FieldLabel>
              <PhoneInput
                name="phone"
                control={control as Control<SendLinkForm>}
                international
                withCountryCallingCode
                onChange={undefined}
                labels={pt_BR}
                limitMaxLength
              />
              <ErrorMessage>{errors.phone && <span>{errors.phone.message}</span>}</ErrorMessage>
            </Field>
            {window.frontTelemedicinaOptions?.messagesFlags.email && (
              <Field>
                <FieldLabel htmlFor="email">
                  <b>E-mail</b>
                </FieldLabel>
                <input
                  id="email"
                  type="email"
                  {...register("email", {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Endereço de e-mail inválido",
                    },
                  })}
                />
                <ErrorMessage>{errors.email && <span>{errors.email.message}</span>}</ErrorMessage>
              </Field>
            )}
          </FieldsWrapper>
          <ButtonsWrapper>
            {window.frontTelemedicinaOptions?.messagesFlags.whatsapp && (
              <ButtonTooltip
                title={watch("phone") === null ? "Forneça um número de celular" : ""}
                arrow
              >
                <ButtonWrapper>
                  <WppButton
                    disabled={watch("phone") === null}
                    onClick={handleSubmit((data: SendLinkForm) => onSubmit(data, SEND_TYPE.WHATSAPP))}
                    endIcon={<FaShareSquare />}
                  >
                    Enviar via Whatsapp
                  </WppButton>
                </ButtonWrapper>
              </ButtonTooltip>
            )}
            {window.frontTelemedicinaOptions?.messagesFlags.sms && (
              <ButtonTooltip
                title={
                  watch("phone") === null
                    ? "Forneça um número de celular"
                    : ""
                }
                arrow
              >
                <ButtonWrapper>
                  <SendButton
                    disabled={watch("phone") === null}
                    onClick={handleSubmit((data: SendLinkForm) =>
                      onSubmit(data, SEND_TYPE.SMS),
                    )}
                    endIcon={<FaShareSquare />}
                  >
                    Enviar SMS
                  </SendButton>
                </ButtonWrapper>
              </ButtonTooltip>
            )}
            {window.frontTelemedicinaOptions?.messagesFlags.email && (
              <ButtonTooltip
                title={
                  watch("email") === ""
                    ? "Forneça um endereço de e-mail"
                    : ""
                }
                arrow
              >
                <ButtonWrapper>
                  <SendButton
                    disabled={watch("email") === ""}
                    onClick={handleSubmit((data: SendLinkForm) =>
                      onSubmit(data, SEND_TYPE.EMAIL),
                    )}
                    endIcon={<FaShareSquare />}
                  >
                    Enviar E-mail
                  </SendButton>
                </ButtonWrapper>
              </ButtonTooltip>
            )}
          </ButtonsWrapper>
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default SendLinkModal;
